/**
 * Template Recommendation Wizard - 5-step LLM-powered recommendation flow
 */
import React, { useState } from 'react';
import {
  Factory, ShoppingCart, Landmark, Monitor, HardHat, HeartPulse,
  Truck, Utensils, ChevronRight, ChevronLeft, Loader2, Sparkles,
  TrendingDown, DollarSign, Calendar, Users, Star, Zap, Target,
  Shield, Award, Leaf, Rocket, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { apiFetch } from '../../services/api_service';


const INDUSTRIES = [
  { id: 'manufacturing', name: 'Manufacturing', icon: Factory, desc: 'Industrial production, factories' },
  { id: 'retail', name: 'Retail & Consumer', icon: ShoppingCart, desc: 'Stores, e-commerce, CPG' },
  { id: 'financial_services', name: 'Financial Services', icon: Landmark, desc: 'Banking, insurance, investment' },
  { id: 'technology', name: 'Technology & SaaS', icon: Monitor, desc: 'Software, hardware, cloud' },
  { id: 'construction', name: 'Construction & Real Estate', icon: HardHat, desc: 'Building, property mgmt' },
  { id: 'healthcare', name: 'Healthcare', icon: HeartPulse, desc: 'Hospitals, medical facilities' },
  { id: 'logistics', name: 'Logistics & Transport', icon: Truck, desc: 'Freight, delivery, warehousing' },
  { id: 'food_beverage', name: 'Food & Beverage', icon: Utensils, desc: 'Food production, distribution' },
];

const REVENUE_OPTIONS = [
  { value: 25000000, label: '$10M - $50M' },
  { value: 75000000, label: '$50M - $100M' },
  { value: 250000000, label: '$100M - $500M' },
  { value: 750000000, label: '$500M - $1B' },
  { value: 2500000000, label: '$1B - $5B' },
  { value: 7500000000, label: '$5B - $10B' },
  { value: 15000000000, label: '$10B+' },
];

const EMPLOYEE_OPTIONS = [
  { value: 75, label: '50 - 100' },
  { value: 250, label: '100 - 500' },
  { value: 1000, label: '500 - 2,000' },
  { value: 5000, label: '2,000 - 10,000' },
  { value: 15000, label: '10,000 - 50,000' },
  { value: 75000, label: '50,000+' },
];

const BUDGET_OPTIONS = [
  { value: 2500000, label: '$1M - $5M' },
  { value: 7500000, label: '$5M - $10M' },
  { value: 17500000, label: '$10M - $25M' },
  { value: 37500000, label: '$25M - $50M' },
  { value: 75000000, label: '$50M - $100M' },
  { value: 150000000, label: '$100M - $200M' },
  { value: 300000000, label: '$200M+' },
];

const INITIATIVE_OPTIONS = [
  { id: 'led_lighting', name: 'LED Lighting Retrofit' },
  { id: 'renewable_energy', name: 'Renewable Energy (any %)' },
  { id: 'ev_fleet', name: 'Electric Vehicles (any %)' },
  { id: 'recycling', name: 'Recycling Program' },
  { id: 'supplier_engagement', name: 'Supplier Engagement' },
  { id: 'green_buildings', name: 'Green Building Certifications' },
  { id: 'waste_reduction', name: 'Waste Reduction Program' },
  { id: 'water_conservation', name: 'Water Conservation' },
  { id: 'none', name: 'None yet - just getting started' },
];

const PRIORITY_OPTIONS = [
  { id: 'cost_savings', name: 'Cost Savings & ROI', icon: DollarSign, desc: 'Projects that pay for themselves' },
  { id: 'quick_wins', name: 'Quick Wins', icon: Zap, desc: '<2 year implementation' },
  { id: 'net_zero', name: 'Net Zero Achievement', icon: Target, desc: 'Achieve carbon neutrality' },
  { id: 'scope_3', name: 'Scope 3 Reduction', icon: Factory, desc: 'Supply chain focus' },
  { id: 'certification', name: 'Certifications', icon: Award, desc: 'LEED, SBTi, etc.' },
  { id: 'resilience', name: 'Climate Resilience', icon: Shield, desc: 'Future-proofing operations' },
  { id: 'social_impact', name: 'Social Impact', icon: Users, desc: 'DEI, wages, community' },
  { id: 'innovation', name: 'Innovation Leadership', icon: Rocket, desc: 'Industry-first initiatives' },
];

const DIFF_COLORS = {
  beginner: 'text-emerald-400 bg-emerald-500/15 border-emerald-500/30',
  intermediate: 'text-amber-400 bg-amber-500/15 border-amber-500/30',
  advanced: 'text-red-400 bg-red-500/15 border-red-500/30',
};

function fmt$(v) {
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(0)}M`;
  return `$${Math.round(v).toLocaleString()}`;
}

export default function RecommendationWizard({ onClose, onUseTemplate }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [profileInsight, setProfileInsight] = useState('');
  const [profile, setProfile] = useState({
    industry: '',
    revenue: 0,
    employees: 0,
    facilities: 10,
    budget: 0,
    timeline: 2030,
    riskTolerance: 'moderate',
    existingInitiatives: [],
    certifications: [],
    priorities: [],
  });

  const updateProfile = (updates) => setProfile(p => ({ ...p, ...updates }));

  const toggleArrayItem = (field, item) => {
    setProfile(p => {
      const arr = p[field];
      if (item === 'none') return { ...p, [field]: ['none'] };
      const filtered = arr.filter(i => i !== 'none');
      return { ...p, [field]: filtered.includes(item) ? filtered.filter(i => i !== item) : [...filtered, item] };
    });
  };

  const getRecommendations = async () => {
    setLoading(true);
    setError(null);
    try {
      const startData = await apiFetch(`/v1/simulations/templates/recommendations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profile }),
      });
      const sessionId = startData.sessionId;
      if (!sessionId) throw new Error('No session ID returned');

      // Poll for results
      for (let attempt = 0; attempt < 30; attempt++) {
        await new Promise(r => setTimeout(r, 2000));
        try {
          const d = await apiFetch(`/v1/simulations/templates/recommendations/${sessionId}`);
          if (d.status === 'completed' && d.recommendations?.length > 0) {
            setRecommendations(d.recommendations);
            setProfileInsight(d.profileInsight || '');
            setStep(6);
            return;
          }
          if (d.status === 'failed') throw new Error(d.error || 'AI analysis failed');
        } catch (err) {
          if (err.message?.includes('AI analysis failed')) throw err;
        }
      }
      throw new Error('Recommendation engine timed out — please try again');
    } catch (e) {
      setError(e.message || 'Failed to generate recommendations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return !!profile.industry;
    if (step === 2) return profile.revenue > 0 && profile.employees > 0;
    if (step === 3) return true;
    if (step === 4) return profile.budget > 0;
    if (step === 5) return profile.priorities.length > 0;
    return true;
  };

  const STEPS = ['Industry', 'Company', 'Initiatives', 'Goals', 'Priorities'];

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="recommendation-wizard">
      <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0D0D0D] border-b border-[#1F1F1F] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-violet-500/15 rounded-lg">
              <Sparkles className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white">AI Template Advisor</h2>
              <p className="text-[11px] text-[#6B6B6B]">Powered by Claude Sonnet 4.6</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#1A1A1A] rounded-lg text-[#6B6B6B] hover:text-white transition-colors" data-testid="wizard-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress */}
        {step <= 5 && (
          <div className="px-6 pt-4">
            <div className="flex items-center gap-1">
              {STEPS.map((s, i) => (
                <React.Fragment key={s}>
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                    i + 1 <= step ? 'bg-violet-600 text-white' : 'bg-[#1A1A1A] text-[#3F3F3F]'
                  }`}>{i + 1}</div>
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 ${i + 1 < step ? 'bg-violet-600' : 'bg-[#1F1F1F]'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="flex justify-between mt-1.5 px-1">
              {STEPS.map(s => <span key={s} className="text-[9px] text-[#3F3F3F]">{s}</span>)}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Industry */}
          {step === 1 && (
            <div data-testid="wizard-step-1">
              <h3 className="text-lg font-bold text-white mb-1">What industry are you in?</h3>
              <p className="text-xs text-[#6B6B6B] mb-5">We'll show templates and benchmarks for your sector</p>
              <div className="grid grid-cols-2 gap-2.5">
                {INDUSTRIES.map(ind => {
                  const Icon = ind.icon;
                  return (
                    <button
                      key={ind.id}
                      onClick={() => { updateProfile({ industry: ind.id }); setStep(2); }}
                      className={`text-left p-4 rounded-xl border transition-all ${
                        profile.industry === ind.id
                          ? 'border-violet-500 bg-violet-500/10'
                          : 'border-[#1F1F1F] hover:border-violet-500/40 bg-[#111111]'
                      }`}
                      data-testid={`wiz-industry-${ind.id}`}
                    >
                      <Icon className={`w-6 h-6 mb-2 ${profile.industry === ind.id ? 'text-violet-400' : 'text-[#6B6B6B]'}`} />
                      <p className="text-sm font-semibold text-white">{ind.name}</p>
                      <p className="text-[10px] text-[#6B6B6B] mt-0.5">{ind.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: Company Size */}
          {step === 2 && (
            <div data-testid="wizard-step-2">
              <h3 className="text-lg font-bold text-white mb-1">Tell us about your company</h3>
              <p className="text-xs text-[#6B6B6B] mb-5">We'll match you with templates designed for similar organizations</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-[#A0A0A0] mb-1.5">Annual Revenue (USD)</label>
                  <select value={profile.revenue} onChange={e => updateProfile({ revenue: +e.target.value })}
                    className="w-full px-3 py-2.5 bg-[#111111] border border-[#2F2F2F] rounded-lg text-sm text-white focus:border-violet-500/50 focus:outline-none"
                    data-testid="wiz-revenue">
                    <option value={0}>Select revenue range...</option>
                    {REVENUE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#A0A0A0] mb-1.5">Number of Employees</label>
                  <select value={profile.employees} onChange={e => updateProfile({ employees: +e.target.value })}
                    className="w-full px-3 py-2.5 bg-[#111111] border border-[#2F2F2F] rounded-lg text-sm text-white focus:border-violet-500/50 focus:outline-none"
                    data-testid="wiz-employees">
                    <option value={0}>Select employee range...</option>
                    {EMPLOYEE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#A0A0A0] mb-1.5">Number of Facilities</label>
                  <input type="number" value={profile.facilities} onChange={e => updateProfile({ facilities: +e.target.value })}
                    placeholder="e.g., 25" min={1}
                    className="w-full px-3 py-2.5 bg-[#111111] border border-[#2F2F2F] rounded-lg text-sm text-white focus:border-violet-500/50 focus:outline-none"
                    data-testid="wiz-facilities" />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Current State */}
          {step === 3 && (
            <div data-testid="wizard-step-3">
              <h3 className="text-lg font-bold text-white mb-1">What have you done so far?</h3>
              <p className="text-xs text-[#6B6B6B] mb-5">Select any sustainability initiatives already in place</p>
              <div className="grid grid-cols-2 gap-2">
                {INITIATIVE_OPTIONS.map(opt => (
                  <button key={opt.id} onClick={() => toggleArrayItem('existingInitiatives', opt.id)}
                    className={`text-left p-3 rounded-lg border transition-all text-sm ${
                      profile.existingInitiatives.includes(opt.id)
                        ? 'border-violet-500 bg-violet-500/10 text-violet-400'
                        : 'border-[#1F1F1F] text-[#A0A0A0] hover:border-violet-500/30'
                    }`}
                    data-testid={`wiz-init-${opt.id}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                        profile.existingInitiatives.includes(opt.id)
                          ? 'border-violet-500 bg-violet-500' : 'border-[#3F3F3F]'
                      }`}>
                        {profile.existingInitiatives.includes(opt.id) && <span className="text-white text-[8px]">&#10003;</span>}
                      </div>
                      {opt.name}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Goals & Budget */}
          {step === 4 && (
            <div data-testid="wizard-step-4">
              <h3 className="text-lg font-bold text-white mb-1">What are your goals?</h3>
              <p className="text-xs text-[#6B6B6B] mb-5">Timeline, budget, and risk appetite</p>
              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold text-[#A0A0A0] mb-2">Target Year</label>
                  <div className="flex gap-2">
                    {[2027, 2030, 2035, 2040, 2050].map(y => (
                      <button key={y} onClick={() => updateProfile({ timeline: y })}
                        className={`flex-1 py-2.5 rounded-lg text-sm font-semibold border transition-all ${
                          profile.timeline === y
                            ? 'border-violet-500 bg-violet-500/15 text-violet-400'
                            : 'border-[#1F1F1F] text-[#6B6B6B] hover:border-violet-500/30'
                        }`} data-testid={`wiz-year-${y}`}>{y}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#A0A0A0] mb-1.5">Total Budget Available</label>
                  <select value={profile.budget} onChange={e => updateProfile({ budget: +e.target.value })}
                    className="w-full px-3 py-2.5 bg-[#111111] border border-[#2F2F2F] rounded-lg text-sm text-white focus:border-violet-500/50 focus:outline-none"
                    data-testid="wiz-budget">
                    <option value={0}>Select budget range...</option>
                    {BUDGET_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#A0A0A0] mb-2">Risk Tolerance</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: 'conservative', label: 'Conservative', sub: 'Proven tech only', icon: Shield },
                      { id: 'moderate', label: 'Moderate', sub: 'Balanced approach', icon: Leaf },
                      { id: 'aggressive', label: 'Aggressive', sub: 'Cutting-edge OK', icon: Rocket },
                    ].map(opt => {
                      const Icon = opt.icon;
                      return (
                        <button key={opt.id} onClick={() => updateProfile({ riskTolerance: opt.id })}
                          className={`p-3 rounded-lg border text-center transition-all ${
                            profile.riskTolerance === opt.id
                              ? 'border-violet-500 bg-violet-500/10' : 'border-[#1F1F1F] hover:border-violet-500/30'
                          }`} data-testid={`wiz-risk-${opt.id}`}>
                          <Icon className={`w-5 h-5 mx-auto mb-1 ${profile.riskTolerance === opt.id ? 'text-violet-400' : 'text-[#6B6B6B]'}`} />
                          <p className="text-xs font-semibold text-white">{opt.label}</p>
                          <p className="text-[9px] text-[#6B6B6B]">{opt.sub}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Priorities */}
          {step === 5 && (
            <div data-testid="wizard-step-5">
              <h3 className="text-lg font-bold text-white mb-1">What matters most?</h3>
              <p className="text-xs text-[#6B6B6B] mb-5">Select up to 3 priorities</p>
              <div className="grid grid-cols-2 gap-2.5">
                {PRIORITY_OPTIONS.map(opt => {
                  const Icon = opt.icon;
                  const selected = profile.priorities.includes(opt.id);
                  const disabled = !selected && profile.priorities.length >= 3;
                  return (
                    <button key={opt.id}
                      onClick={() => !disabled && toggleArrayItem('priorities', opt.id)}
                      disabled={disabled}
                      className={`text-left p-4 rounded-xl border transition-all ${
                        selected ? 'border-violet-500 bg-violet-500/10'
                          : disabled ? 'border-[#1F1F1F] opacity-30 cursor-not-allowed'
                          : 'border-[#1F1F1F] hover:border-violet-500/30'
                      }`} data-testid={`wiz-priority-${opt.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <Icon className={`w-5 h-5 ${selected ? 'text-violet-400' : 'text-[#6B6B6B]'}`} />
                        {selected && <div className="w-5 h-5 rounded-full bg-violet-600 flex items-center justify-center text-white text-[9px]">&#10003;</div>}
                      </div>
                      <p className="text-sm font-semibold text-white">{opt.name}</p>
                      <p className="text-[10px] text-[#6B6B6B]">{opt.desc}</p>
                    </button>
                  );
                })}
              </div>
              <p className="text-center text-[10px] text-[#3F3F3F] mt-3">{profile.priorities.length}/3 selected</p>
            </div>
          )}

          {/* Step 6: Results */}
          {step === 6 && recommendations && (
            <div data-testid="wizard-step-results">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-violet-500/15 rounded-2xl mb-3">
                  <Target className="w-7 h-7 text-violet-400" />
                </div>
                <h3 className="text-lg font-bold text-white">Your Top Recommendations</h3>
                {profileInsight && <p className="text-xs text-[#6B6B6B] mt-1 max-w-md mx-auto">{profileInsight}</p>}
              </div>
              <div className="space-y-3">
                {recommendations.map((rec, idx) => (
                  <RecCard key={rec.template.templateId} rec={rec} rank={idx + 1} onUse={() => onUseTemplate(rec.template.templateId)} />
                ))}
              </div>
              {recommendations.length === 0 && (
                <p className="text-center text-sm text-[#6B6B6B] py-8">No matching templates found. Try adjusting your profile.</p>
              )}
            </div>
          )}

          {/* Error */}
          {error && <p className="text-red-400 text-sm text-center mt-3" data-testid="wizard-error">{error}</p>}
        </div>

        {/* Footer Nav */}
        <div className="sticky bottom-0 bg-[#0D0D0D] border-t border-[#1F1F1F] px-6 py-3 flex items-center justify-between">
          {step > 1 && step <= 5 ? (
            <Button variant="ghost" className="text-[#6B6B6B]" onClick={() => setStep(s => s - 1)} data-testid="wizard-back">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          ) : step === 6 ? (
            <Button variant="ghost" className="text-[#6B6B6B]" onClick={() => { setStep(1); setRecommendations(null); }} data-testid="wizard-restart">
              Start Over
            </Button>
          ) : <div />}

          {step >= 2 && step <= 4 && (
            <Button onClick={() => setStep(s => s + 1)} disabled={!canProceed()}
              className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="wizard-next">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
          {step === 5 && (
            <Button onClick={getRecommendations} disabled={!canProceed() || loading}
              className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="wizard-get-recs">
              {loading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Analyzing...</> : <><Sparkles className="w-4 h-4 mr-1.5" /> Get Recommendations</>}
            </Button>
          )}
          {step === 6 && (
            <Button variant="outline" className="border-[#2F2F2F] text-[#A0A0A0]" onClick={onClose} data-testid="wizard-browse-all">
              Browse All Templates
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function RecCard({ rec, rank, onUse }) {
  const { template: t, score, matchReasons, customizations } = rec;
  const er = t.expectedResults || {};
  const costRange = er.financialSummary?.totalCostRange;
  const rankColors = { 1: 'from-amber-500 to-amber-700', 2: 'from-slate-400 to-slate-600', 3: 'from-orange-500 to-orange-700' };
  const rankBg = rankColors[rank] || 'from-violet-500 to-violet-700';

  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl hover:border-violet-500/30 transition-all" data-testid={`rec-card-${rank}`}>
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${rankBg} flex items-center justify-center text-white font-bold text-sm shrink-0`}>
            #{rank}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-sm font-bold text-white truncate">{t.templateName}</h4>
              <Badge className={`${DIFF_COLORS[t.difficulty]} text-[9px] shrink-0`}>{t.difficulty}</Badge>
            </div>
            <p className="text-[11px] text-[#6B6B6B] line-clamp-1">{t.templateDescription}</p>
            {/* Score bar */}
            <div className="flex items-center gap-2 mt-2">
              <div className="h-1.5 w-24 bg-[#1F1F1F] rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-violet-500 to-blue-500 rounded-full" style={{ width: `${score}%` }} />
              </div>
              <span className="text-[11px] font-bold text-violet-400">{score}% Match</span>
            </div>
          </div>
        </div>

        {/* Metrics row */}
        <div className="grid grid-cols-4 gap-2 mt-3">
          <Metric icon={TrendingDown} color="text-emerald-400" value={`${er.emissionsReduction?.percentageReduction || 0}%`} label="Reduction" />
          <Metric icon={DollarSign} color="text-blue-400" value={costRange ? `${fmt$(costRange.min)}-${fmt$(costRange.max)}` : 'N/A'} label="Investment" />
          <Metric icon={Calendar} color="text-purple-400" value={`${t.target?.targetYear || 2030}`} label="Target" />
          <Metric icon={Users} color="text-[#A0A0A0]" value={`${Math.round((t.industryBenchmark?.peerAdoptionRate || 0) * 100)}%`} label="Adoption" />
        </div>

        {/* Match reasons */}
        {matchReasons?.length > 0 && (
          <div className="mt-3 space-y-1">
            {matchReasons.map((r, i) => (
              <div key={i} className="flex items-start gap-1.5 text-[11px]">
                <span className="text-emerald-400 mt-px shrink-0">&#10003;</span>
                <span className="text-[#A0A0A0]">{r}</span>
              </div>
            ))}
          </div>
        )}

        {/* Customization suggestions */}
        {customizations?.length > 0 && (
          <div className="mt-2 space-y-1">
            {customizations.map((c, i) => (
              <div key={i} className="text-[10px] text-blue-400 bg-blue-500/10 border border-blue-500/20 rounded-lg px-2.5 py-1.5">
                {c.description}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-[#1F1F1F] p-3">
        <button onClick={onUse}
          className="w-full py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium transition-colors flex items-center justify-center gap-1.5"
          data-testid={`rec-use-${rank}`}>
          <Sparkles className="w-3.5 h-3.5" /> Use This Template
        </button>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, color, value, label }) {
  return (
    <div className="text-center p-1.5 bg-[#0D0D0D] rounded-lg">
      <Icon className={`w-3 h-3 mx-auto mb-0.5 ${color}`} />
      <p className={`text-xs font-bold ${color}`}>{value}</p>
      <p className="text-[8px] text-[#3F3F3F]">{label}</p>
    </div>
  );
}
